/**
 * pi-nonograms - A game of nonograms
 * https://github.com/DomenicoDeFelice/Pi-Nonograms
 *
 * Copyright (c) 2013-2020 Domenico De Felice
 * Released under the GNU GPL v3 License
 *
 * @license
 * @format
 * @flow
 */

import type { JQuery } from 'jquery';
import type { Cell, Definition } from './Model.js';

const Srand = require('seeded-rand');
const Model = require('./Model.js');
const Event = require('./Event.js');
const $ = require('jquery');

type ViewEvents = {|
  mouseDownOnCell: Event,
  mouseUp: Event,
  mouseEntersCell: Event,
  mouseLeavesCell: Event,
|};

class View {
  events: ViewEvents = {
    mouseDownOnCell: new Event(this),
    mouseUp: new Event(this),
    mouseEntersCell: new Event(this),
    mouseLeavesCell: new Event(this),
  };

  _id: string;
  _model: Model;
  _$container: JQuery<HTMLElement>;
  _$nonogram: JQuery<HTMLElement>;
  _theme: string;

  constructor(model: Model, container: HTMLElement) {
    this._model = model;
    this._$container = $(container);
    this._$nonogram = null;
    this._theme = 'classic';

    let id;
    do {
      id = 'nonogram-' + Srand.intInRange(0, 1000000);
    } while ($('#' + id).length > 0);
    this._id = id;
  }

  show = (): void => {
    this.rebuildNonogram();
  };

  setSolved = (): void => {
    this._$nonogram.removeClass('nonogram_playing').addClass('nonogram_solved');
  };

  setUnsolved = () => {
    this._$nonogram.removeClass('nonogram_solved').addClass('nonogram_playing');
  };

  setTheme = (theme: string): void => {
    if (this._$nonogram != null) {
      this._$nonogram.removeClass(this._theme).addClass(theme);
    }
    this._theme = theme;
  };

  highlightColumn = (col: number): void => {
    this._$container
      .find('.nonogram_column_' + col + '_cell')
      .addClass('nonogram_hovered_column');
    this._$container
      .find('#' + this._idOfColumnDefinition(col))
      .addClass('nonogram_hovered_column');
  };

  unhighlightColumn = (col: number): void => {
    this._$container
      .find('.nonogram_column_' + col + '_cell')
      .removeClass('nonogram_hovered_column');
    this._$container
      .find('#' + this._idOfColumnDefinition(col))
      .removeClass('nonogram_hovered_column');
  };

  setGuessAt = (x: number, y: number, newGuess: Cell): void => {
    const cell = $('#' + this._idOfCell(x, y));
    const oldGuess = cell.data().guess;

    cell
      .removeClass('nonogram_correct_guess')
      .removeClass(this._guessToCSSClass(oldGuess))
      .addClass(this._guessToCSSClass(newGuess))
      .data({ guess: newGuess });

    if (this._model.getCellAt(x, y) === newGuess) {
      cell.addClass('nonogram_correct_guess');
    }

    // Update row & column definitions
    $('#' + this._idOfRowDefinition(y)).html(
      this._rowDefinitionToHTML(this._model.getRowDefinition(y))
    );
    $('#' + this._idOfColumnDefinition(x)).html(
      this._columnDefinitionToHTML(this._model.getColumnDefinition(x))
    );
  };

  rebuildNonogram = (): void => {
    const width = this._model.width;
    const height = this._model.height;
    let x, y, tr;

    const table = (this._$nonogram = $('<table>')
      .attr('id', this._id)
      .addClass('nonogram')
      .addClass(this._theme));

    if (this._model.isSolved()) {
      table.addClass('nonogram_solved');
    } else {
      table.addClass('nonogram_playing');
    }

    // Column Definitions Row
    tr = $('<tr>').addClass('nonogram_row');

    // Top Left cell
    $('<td>').addClass('nonogram_top_left_cell').appendTo(tr);

    for (let x = 0; x < width; x++) {
      if (x && x % 5 === 0) {
        $('<td>').addClass('nonogram_separation_column').appendTo(tr);
      }

      $('<td>')
        .attr('id', this._idOfColumnDefinition(x))
        .addClass('nonogram_definition nonogram_column_definition')
        .html(this._columnDefinitionToHTML(this._model.getColumnDefinition(x)))
        .appendTo(tr);
    }
    tr.appendTo(table);

    for (let y = 0; y < height; y++) {
      // Separate groups of five rows
      if (y && y % 5 === 0) {
        $('<tr>')
          .addClass('nonogram_separation_row')
          .append($('<td colspan="' + (width + width - 1) + '">'))
          .appendTo(table);
      }

      // Create new row
      tr = $('<tr>').addClass('nonogram_row');

      // Create definition for the current row
      $('<td>')
        .attr('id', this._idOfRowDefinition(y))
        .addClass('nonogram_definition nonogram_row_definition')
        .html(this._rowDefinitionToHTML(this._model.getRowDefinition(y)))
        .appendTo(tr);

      for (let x = 0; x < width; x++) {
        // Separate groups of five columns
        if (x && x % 5 === 0) {
          $('<td>').addClass('nonogram_separation_column').appendTo(tr);
        }

        // Build the actual nonogram cell
        $('<td>')
          .attr('id', this._idOfCell(x, y))
          .addClass(this._cssClassesForCell(x, y))
          .data({
            x: x,
            y: y,
            guess: this._model.getGuessAt(x, y),
          })
          .appendTo(tr);
      }
      tr.appendTo(table);
    }

    this._setupEventHandlers(table);

    this._$container.finish().hide().empty().append(table).show();
  };

  // Private methods

  _setupEventHandlers = ($target: JQuery<HTMLElement>): void => {
    $target.on('mousedown', 'td', (e: MouseEvent) => {
      // Only take in consideration main button clicks
      if (e.button !== 0) {
        return;
      }

      e.preventDefault();

      const cellData = $(e.target).data();
      this.events.mouseDownOnCell.notify(cellData);
    });

    const mouseUpHandler = () => {
      // Has the nonogram been removed from the DOM?
      if (!$.contains(document, $target[0])) {
        // If it has been removed, unbind this event handler
        // from document to avoid memory leaks (the references
        // to `view` and `$target` make it impossible for the
        // garbage collector to free them).
        $(document).unbind('mouseup', mouseUpHandler);
      } else {
        this.events.mouseUp.notify();
      }
    };
    $(document).on('mouseup', mouseUpHandler);

    $target.on('mouseover', 'td', (e: MouseEvent) => {
      e.preventDefault();

      const cellData = $(e.target).data();
      this.events.mouseEntersCell.notify(cellData);
    });

    $target.on('mouseout', 'td', (e: MouseEvent) => {
      e.preventDefault();

      const cellData = $(e.target).data();
      this.events.mouseLeavesCell.notify(cellData);
    });
  };

  _idOfCell = (x: number, y: number): string => {
    return this._id + '_x_' + x + '_y_' + y;
  };

  _idOfRowDefinition = (row: number): string => {
    return this._id + '_row_' + row + '_definition';
  };

  _idOfColumnDefinition = (col: number): string => {
    return this._id + '_column_' + col + '_definition';
  };

  _rowDefinitionToHTML = (sequences: Definition): string => {
    let html = '<nobr>';

    for (let i = 0, n = sequences.length; i < n; i++) {
      if (i > 0) {
        html += '&nbsp;';
      }

      html += '<span class="nonogram_sequence';

      if (sequences[i].solved) {
        html += ' nonogram_solved_sequence';
      }

      html += '">' + sequences[i].length + '</span>';
    }

    html += '</nobr>';
    return html;
  };

  _columnDefinitionToHTML = (sequences: Definition): string => {
    let html = '';

    for (let i = 0, n = sequences.length; i < n; i++) {
      if (i > 0) {
        html += '<br>';
      }

      html += '<nobr><span class="nonogram_sequence';

      if (sequences[i].solved) {
        html += ' nonogram_solved_sequence';
      }

      html += '">' + sequences[i].length + '</span></nobr>';
    }

    return html;
  };

  _cssClassesForCell = (x: number, y: number): string => {
    const cellGuess = this._model.getGuessAt(x, y);
    const actualCell = this._model.getCellAt(x, y);

    const classes = [];
    classes.push('nonogram_cell');
    classes.push(`nonogram_column_${x}_cell`);
    classes.push(this._guessToCSSClass(cellGuess));

    if (cellGuess === actualCell) {
      classes.push('nonogram_correct_guess');
    }

    return classes.join(' ');
  };

  _guessToCSSClass = (guess: Cell): string => {
    return `nonogram_${guess}_cell`;
  };
}

module.exports = View;
